import { CustomCardStyle } from "@/components/mocks/components/CustomCardStyle";
import NetBurn from "@/components/static-dashboard-components/charts-components/NetBurn";
import NetBurnAndBalanceChart from "@/components/static-dashboard-components/charts-components/NetBurnAndBalanceChart";
import { RunwayRunRateChart } from "@/components/static-dashboard-components/charts-components/RunwayRunRateChart";
import { SpendingChart } from "@/components/static-dashboard-components/charts-components/SpendingChart";
import { TodayCash } from "@/components/static-dashboard-components/charts-components/TodayCash";
import { Colors } from "@/enums/Colors";
import { Chart } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/charts/Chart";
import { ChartType } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/enums/charts/ChartType";
import { Box, BoxProps, Card, Paper, styled } from "@mui/material";
import { useEffect, useRef, useState } from "react";

enum ChartColorByTitle {
  SPENDING = Colors.LAZO_PURPLE,
  REVENUE = Colors.GREEN_01A902,
}

function CustomChartCard({ chart, containerProps }: { chart: Chart; containerProps?: BoxProps }) {
  const StyledPaper = styled(Paper)(({ theme }) => ({
    borderRadius: "1.5rem",
    paddingBottom: "1.5rem",
    margin: "auto",
  }));
  const netBurnRef = useRef(null);
  const barRef = useRef(null);
  const [screenshot, setScreenshot] = useState(null);

  const chartToRender = () => {
    switch (chart.type) {
      case ChartType.BARCHART:
        return (
          <SpendingChart
            data={chart.content}
            disableAskAIButton={true}
            containerProps={{ padding: "0.5rem" }}
            refer={barRef}
            chartOptions={{
              animation: false,
              plugins: {
                legend: {
                  display: false,
                },
              },
              elements: {
                bar: {
                  borderRadius: 2,
                  backgroundColor: ChartColorByTitle[chart.title],
                },
              },
              scales: {
                x: {
                  grid: {
                    display: false,
                  },
                  border: {
                    display: true,
                  },
                },
                y: {
                  display: true,
                  border: {
                    display: true,
                  },
                },
              },
            }}
          />
        );
      case ChartType.LINECHART:
        return (
          <NetBurnAndBalanceChart
            data={chart.content}
            containerProps={{ padding: "0.5rem" }}
            disableAskAIButton={true}
            refer={netBurnRef}
            chartOptions={{
              animation: false,
              plugins: {
                legend: {
                  display: true,
                  labels: {
                    usePointStyle: true,
                  },
                },
              },
            }}
          />
        );
      case ChartType.TODAYS_CASH:
        return (
          <StyledPaper style={{ width: "90%", height: "90%" }}>
            <TodayCash data={chart.content} disableAskAIButton={true} disableExpand={true} />
          </StyledPaper>
        );
      case ChartType.NET_BURN:
        return (
          <StyledPaper style={{ width: "90%", height: "90%" }}>
            <NetBurn data={chart.content} disableAskAIButton={true} disableExpand={true} />
          </StyledPaper>
        );
      case ChartType.MONTHS_LEFT_CHART:
        return (
          <StyledPaper style={{ width: "90%", height: "90%" }}>
            <RunwayRunRateChart data={chart.content} disableAskAIButton={true} />
          </StyledPaper>
        );
      default:
        throw Error("ChartType Not Defined");
    }
  };

  useEffect(() => {
    const getImage = (e) => {
      const base64Image = e.toBase64Image("image/png", 1);
      setScreenshot(base64Image);
    };
    if (netBurnRef.current) {
      getImage(netBurnRef.current);
    } else if (barRef.current) {
      getImage(barRef.current);
    } else {
      setScreenshot(null);
    }
  }, [chart.title, netBurnRef.current, barRef.current]);

  const autoHeight = [ChartType.MONTHS_LEFT_CHART, ChartType.NET_BURN, ChartType.TODAYS_CASH].includes(chart.type);

  const getVisibility = [ChartType.LINECHART, ChartType.BARCHART].includes(chart.type);

  return (
    <Box
      sx={{
        height: autoHeight ? "auto" : "150px",
      }}
      {...containerProps}
    >
      <Card sx={{ ...CustomCardStyle }} variant="outlined">
        {getVisibility && screenshot && <img src={screenshot} alt="chart" style={{ width: "100%", height: "auto", marginTop: "10px" }} />}
        <div
          style={{
            visibility: getVisibility ? "hidden" : "visible",
            width: getVisibility ? "40rem" : "100%",
            height: autoHeight ? "auto" : "300px",
          }}
        >
          {chartToRender()}
        </div>
      </Card>
    </Box>
  );
}

export default CustomChartCard;
